import React, { useState, useEffect } from "react";
import { Bot, Play, Loader2, Save, BellRing } from "lucide-react";
import { getMailAgentSettings, updateMailAgentSettings, runMailAgent, runRdvReminders } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { inputCls } from "./mailUtils";

const MailAgentSettings = ({ isMaster, majorsCount, onRan }) => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const { toast } = useToast();

  useEffect(() => { getMailAgentSettings().then((r) => setS(r.data)).catch(() => setS(null)); }, []);

  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    setSaving(true);
    try {
      const r = await updateMailAgentSettings(next);
      setS(r.data);
      toast({ title: "Agent mail mis à jour ✓" });
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const run = async () => {
    setRunning(true);
    try {
      const r = await runMailAgent();
      const n = r.data.processed?.length || 0;
      toast({ title: "Agent exécuté ✓", description: `${r.data.sync?.new || 0} nouveau(x) email(s) relevé(s), ${n} traité(s) par l'IA.` });
      onRan?.();
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec", variant: "destructive" });
    } finally { setRunning(false); }
  };

  const [reminding, setReminding] = useState(false);
  const remind = async () => {
    setReminding(true);
    try {
      const r = await runRdvReminders();
      toast({ title: "Rappels J-1 envoyés", description: `${r.data.sent} major(s) prévenu(s) sur ${r.data.majors} avec RDV confirmés demain.` });
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec", variant: "destructive" });
    } finally { setReminding(false); }
  };

  if (!s) return null;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="mail-agent-settings">
      <div className="flex items-center gap-2 mb-2">
        <Bot size={17} className="text-violet-600" />
        <h3 className="font-semibold text-neutral-900 text-sm">Agent IA de traitement des emails</h3>
        <span className={`ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full ${s.enabled ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`} data-testid="mail-agent-status">
          {s.enabled ? (s.mode === "auto" ? "Actif · automatique" : "Actif · suggestions") : "Désactivé"}
        </span>
      </div>
      <p className="text-xs text-neutral-500 mb-3">
        Toutes les 2 min : relève la boîte, classe chaque email (RDV, devis, refus…), rédige une réponse et propose un
        créneau selon les disponibilités des majors ({majorsCount} configuré{majorsCount > 1 ? "s" : ""}).
        En mode <b>automatique</b>, il répond et pose le RDV dans l'agenda (CRM + Dolibarr) sans validation. Quand le prospect répond « OK pour ce créneau », le RDV est confirmé et le major prévenu par email ; les emails urgents ou demandes de devis déclenchent une alerte immédiate au super-admin. La veille (à l'heure des rappels de planning, onglet Interventions), chaque major reçoit ses RDV confirmés du lendemain avec les coordonnées du prospect.
      </p>
      {isMaster ? (
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-neutral-800">
              <input type="checkbox" className="accent-brand w-4 h-4" checked={!!s.enabled} data-testid="mail-agent-enabled" onChange={(e) => save({ enabled: e.target.checked })} />
              Activer l'agent
            </label>
            <label className="flex items-center gap-2 text-sm text-neutral-800">
              <input type="checkbox" className="accent-brand w-4 h-4" checked={s.alert_admin !== false} data-testid="mail-agent-alert" onChange={(e) => save({ alert_admin: e.target.checked })} />
              Alerter le super-admin (urgent / devis)
            </label>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1">Mode</label>
            <select className={inputCls} value={s.mode} data-testid="mail-agent-mode" onChange={(e) => save({ mode: e.target.value })}>
              <option value="suggestion">Suggestion (je valide chaque RDV)</option>
              <option value="auto">Automatique (répond & planifie seul)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1">Horizon des créneaux (jours)</label>
            <input type="number" min={1} max={30} className={inputCls} value={s.days_ahead} data-testid="mail-agent-days"
              onChange={(e) => setS({ ...s, days_ahead: e.target.value })} onBlur={() => save({ days_ahead: parseInt(s.days_ahead, 10) || 10 })} />
          </div>
        </div>
      ) : (
        <p className="text-xs text-neutral-500">Réglages réservés au super-administrateur.</p>
      )}
      <div className="flex items-center gap-3 mt-3">
        <button onClick={run} disabled={running} data-testid="mail-agent-run-btn"
          className="flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:text-violet-900 disabled:opacity-60">
          {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Relever & traiter maintenant
        </button>
        <button onClick={remind} disabled={reminding} data-testid="mail-agent-rdv-reminders-btn"
          className="flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-brand disabled:opacity-60">
          {reminding ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />} Envoyer les rappels RDV J-1 aux majors
        </button>
        {saving && <span className="text-xs text-neutral-400 flex items-center gap-1"><Save size={12} /> enregistrement…</span>}
      </div>
    </div>
  );
};

export default MailAgentSettings;
